﻿/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  StatusBar,
  Alert,
  Dimensions,
  TextInput,
  FlatList,
  Modal,
  RefreshControl,
  ActivityIndicator,
  Platform,
  Linking,
  Share,
  Clipboard,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import NetInfo from '@react-native-community/netinfo';
import {
  MaterialCommunityIcons,
} from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import { LinearGradient } from 'expo-linear-gradient';
import { SafeAreaView } from 'react-native-safe-area-context';
import { WebView } from 'react-native-webview';

const { width } = Dimensions.get('window');

// ─── Connection ──────────────────────────────────────────────────────────────
const RP_API_BASE = 'https://api.jobeng.ai';
const RP_API_KEY  = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';

const rpStr = (v) => String(v ?? '').trim();
const rpArr = (v) => {
  if (Array.isArray(v)) return v;
  if (typeof v === 'string' && v.trim()) {
    try { const p = JSON.parse(v); if (Array.isArray(p)) return p; } catch {}
    return v.split(',').map((s) => s.trim()).filter(Boolean);
  }
  return [];
};

// Map a DB row from /api/data/research_papers to the paper shape this screen uses.
const rpMapPaper = (row, idx) => ({
  id: rpStr(row.id ?? row.paper_id) || `RP${idx + 1}`,
  title: rpStr(row.title) || 'Untitled Paper',
  authors: rpArr(row.authors),
  university: rpStr(row.university) || '',
  year: rpStr(row.year) || '',
  journal: rpStr(row.journal) || '',
  volume: rpStr(row.volume) || '',
  issue: rpStr(row.issue) || '',
  pages: rpStr(row.pages) || '',
  doi: rpStr(row.doi) || '',
  category: rpStr(row.category) || 'general',
  abstract: rpStr(row.abstract) || '',
  citationCount: Number(row.citation_count ?? row.citationCount ?? 0) || 0,
  downloadCount: Number(row.download_count ?? row.downloadCount ?? 0) || 0,
  pdfUrl: rpStr(row.pdf_url ?? row.pdfUrl) || '',
  keywords: rpArr(row.keywords),
  impactFactor: rpStr(row.impact_factor ?? row.impactFactor) || '',
  isOpenAccess: row.is_open_access === true || row.isOpenAccess === true,
  featured: row.featured === true,
});

const ResearchPapers = () => {
  const navigation = useNavigation();
  const [isOnline, setIsOnline] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [darkMode, setDarkMode] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredPapers, setFilteredPapers] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedYear, setSelectedYear] = useState('all');
  const [selectedUniversity, setSelectedUniversity] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [bookmarkedPapers, setBookmarkedPapers] = useState([]);
  const [downloadedPapers, setDownloadedPapers] = useState([]);
  const [viewMode, setViewMode] = useState('grid');
  const [sortBy, setSortBy] = useState('date');
  
  const [pdfModalVisible, setPdfModalVisible] = useState(false);
  const [selectedPaper, setSelectedPaper] = useState(null);
  const [summaryModalVisible, setSummaryModalVisible] = useState(false);
  
  const [aiSummary, setAiSummary] = useState('');
  const [generatingSummary, setGeneratingSummary] = useState(false);

  const searchTimeoutRef = useRef(null);
  const flatListRef = useRef(null);

  const categories = [
    { id: 'all', label: 'All', icon: 'grid' },
    { id: 'mechanical', label: 'Mechanical', icon: 'cog' },
    { id: 'civil', label: 'Civil', icon: 'bridge' },
    { id: 'electrical', label: 'Electrical', icon: 'flash' },
    { id: 'computer', label: 'Computer', icon: 'laptop' },
    { id: 'chemical', label: 'Chemical', icon: 'flask' },
    { id: 'aerospace', label: 'Aerospace', icon: 'airplane' },
    { id: 'biomedical', label: 'Biomedical', icon: 'heart-pulse' },
  ];

  const years = [
    { id: 'all', label: 'All Years' },
    { id: '2024', label: '2024' },
    { id: '2023', label: '2023' },
    { id: '2022', label: '2022' },
    { id: '2021', label: '2021' },
    { id: '2020', label: '2020' },
    { id: '2019', label: '2019' },
    { id: 'older', label: 'Older' },
  ];

  const universities = [
    { id: 'all', label: 'All' },
    { id: 'MIT', label: 'MIT' },
    { id: 'Stanford', label: 'Stanford' },
    { id: 'Cambridge', label: 'Cambridge' },
    { id: 'Oxford', label: 'Oxford' },
    { id: 'Imperial', label: 'Imperial' },
    { id: 'ETH', label: 'ETH' },
    { id: 'Caltech', label: 'Caltech' },
    { id: 'Harvard', label: 'Harvard' },
  ];

  const [researchPapers, setResearchPapers] = useState([
    {
      id: 'RP001',
      title: 'Advanced Composite Materials for Aerospace Applications',
      authors: ['Dr. Michael Johnson', 'Prof. Sarah Chen', 'Dr. Robert Kim'],
      university: 'MIT',
      year: '2024',
      journal: 'Advanced Materials',
      volume: '36',
      issue: '4',
      pages: '1024-1038',
      doi: '10.1002/adma.202312345',
      category: 'aerospace',
      abstract: 'This paper explores novel composite materials with enhanced strength-to-weight ratios for next-generation aerospace applications.',
      citationCount: 124,
      downloadCount: 856,
      pdfUrl: 'https://arxiv.org/pdf/2401.12345.pdf',
      keywords: ['composites', 'aerospace', 'materials', 'graphene'],
      impactFactor: '9.5',
      isOpenAccess: true,
      featured: true,
    },
    {
      id: 'RP002',
      title: 'Machine Learning Approaches for Structural Health Monitoring',
      authors: ['Prof. David Wilson', 'Dr. Emma Thompson', 'Dr. James Rodriguez'],
      university: 'Stanford',
      year: '2023',
      journal: 'Structural Engineering',
      volume: '45',
      issue: '3',
      pages: '567-582',
      doi: '10.1016/j.str.2023.04.012',
      category: 'civil',
      abstract: 'This research presents a novel deep learning framework for real-time structural health monitoring.',
      citationCount: 89,
      downloadCount: 1245,
      pdfUrl: 'https://arxiv.org/pdf/2307.04567.pdf',
      keywords: ['machine learning', 'structural monitoring', 'deep learning'],
      impactFactor: '8.2',
      isOpenAccess: true,
      featured: true,
    },
    {
      id: 'RP003',
      title: 'Quantum Computing Algorithms for Engineering Optimization',
      authors: ['Dr. Alexei Petrov', 'Prof. Maria Gonzalez'],
      university: 'Cambridge',
      year: '2024',
      journal: 'Quantum Engineering',
      volume: '12',
      issue: '1',
      pages: '45-67',
      doi: '10.1109/QE.2024.01.005',
      category: 'computer',
      abstract: 'Development of quantum algorithms for solving complex engineering optimization problems.',
      citationCount: 67,
      downloadCount: 923,
      pdfUrl: 'https://arxiv.org/pdf/2402.03456.pdf',
      keywords: ['quantum computing', 'optimization', 'algorithms'],
      impactFactor: '10.1',
      isOpenAccess: false,
      featured: true,
    },
    {
      id: 'RP004',
      title: 'Sustainable Energy Solutions for Urban Infrastructure',
      authors: ['Dr. Lisa Wang', 'Prof. Thomas Müller', 'Dr. Ahmed Hassan'],
      university: 'ETH',
      year: '2023',
      journal: 'Sustainable Engineering',
      volume: '28',
      issue: '2',
      pages: '234-250',
      doi: '10.1080/12345678.2023.1234567',
      category: 'electrical',
      abstract: 'Analysis of integrated renewable energy systems for smart cities.',
      citationCount: 156,
      downloadCount: 1876,
      pdfUrl: 'https://arxiv.org/pdf/2305.06789.pdf',
      keywords: ['sustainable energy', 'renewables', 'smart cities'],
      impactFactor: '7.8',
      isOpenAccess: true,
      featured: false,
    },
    {
      id: 'RP005',
      title: 'Biodegradable Polymers for Medical Implants',
      authors: ['Dr. Sophia Lee', 'Prof. Robert Chen', 'Dr. James Wilson'],
      university: 'Harvard',
      year: '2024',
      journal: 'Biomedical Engineering',
      volume: '41',
      issue: '5',
      pages: '789-802',
      doi: '10.1002/bme.202412345',
      category: 'biomedical',
      abstract: 'Development of biodegradable polymer composites for medical implants.',
      citationCount: 42,
      downloadCount: 567,
      pdfUrl: 'https://arxiv.org/pdf/2403.01234.pdf',
      keywords: ['biodegradable', 'polymers', 'medical implants'],
      impactFactor: '8.9',
      isOpenAccess: true,
      featured: false,
    },
  ]);

  useEffect(() => {
    loadInitialData();
    setupNetInfoListener();
  }, []);

  useEffect(() => {
    filterPapers();
  }, [searchQuery, selectedCategory, selectedYear, selectedUniversity, sortBy, researchPapers]);

  const setupNetInfoListener = () => {
    const unsubscribe = NetInfo.addEventListener(state => {
      setIsOnline(state.isConnected);
    });
    return unsubscribe;
  };

  const loadInitialData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadDarkModePreference(),
        loadBookmarks(),
        loadDownloads(),
        loadPapers(),
      ]);
      filterPapers();
    } catch (error) {
      console.error('Error loading initial data:', error);
      setError('Failed to load research papers');
    } finally {
      setLoading(false);
    }
  };

  // Fetch papers from the backend; keep the seed already in state on failure.
  const loadPapers = async () => {
    try {
      const res = await fetch(`${RP_API_BASE}/api/data/research_papers?limit=200`, {
        headers: { 'x-api-key': RP_API_KEY },
      }).then((r) => r.json()).catch(() => null);
      if (res && res.success && Array.isArray(res.data) && res.data.length) {
        setResearchPapers(res.data.map(rpMapPaper));
      }
    } catch (e) {
      // keep seed
    }
  };

  const loadDarkModePreference = async () => {
    try {
      const darkModePref = await AsyncStorage.getItem('darkMode');
      if (darkModePref !== null) {
        setDarkMode(JSON.parse(darkModePref));
      }
    } catch (error) {
      console.error('Error loading dark mode preference:', error);
    }
  };

  const loadBookmarks = async () => {
    try {
      const bookmarks = await AsyncStorage.getItem('bookmarkedPapers');
      if (bookmarks) {
        setBookmarkedPapers(JSON.parse(bookmarks));
      }
    } catch (error) {
      console.error('Error loading bookmarks:', error);
    }
  };

  const loadDownloads = async () => {
    try {
      const downloads = await AsyncStorage.getItem('downloadedPapers');
      if (downloads) {
        setDownloadedPapers(JSON.parse(downloads));
      }
    } catch (error) {
      console.error('Error loading downloads:', error);
    }
  };

  const refreshData = async () => {
    if (!isOnline) return;
    
    setIsRefreshing(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      filterPapers();
    } catch (error) {
      console.error('Error refreshing data:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const filterPapers = () => {
    let filtered = [...researchPapers];
    
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(paper =>
        String(paper.title ?? '').toLowerCase().includes(query) ||
        String(paper.abstract ?? '').toLowerCase().includes(query) ||
        paper.authors.some(author => author.toLowerCase().includes(query)) ||
        paper.keywords.some(keyword => keyword.toLowerCase().includes(query))
      );
    }
    
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(paper => paper.category === selectedCategory);
    }
    
    if (selectedYear !== 'all') {
      if (selectedYear === 'older') {
        filtered = filtered.filter(paper => parseInt(paper.year) < 2019);
      } else {
        filtered = filtered.filter(paper => paper.year === selectedYear);
      }
    }
    
    if (selectedUniversity !== 'all') {
      filtered = filtered.filter(paper => paper.university === selectedUniversity);
    }
    
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'date':
          return b.year.localeCompare(a.year);
        case 'title':
          return a.title.localeCompare(b.title);
        case 'citations':
          return b.citationCount - a.citationCount;
        case 'downloads':
          return b.downloadCount - a.downloadCount;
        default:
          return 0;
      }
    });
    
    setFilteredPapers(filtered);
  };

  const handleSearch = (text) => {
    setSearchQuery(text);
    
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      filterPapers();
    }, 300);
  };

  const toggleBookmark = async (paperId) => {
    try {
      let newBookmarks;
      if (bookmarkedPapers.includes(paperId)) {
        newBookmarks = bookmarkedPapers.filter(id => id !== paperId);
      } else {
        newBookmarks = [...bookmarkedPapers, paperId];
      }
      
      setBookmarkedPapers(newBookmarks);
      await AsyncStorage.setItem('bookmarkedPapers', JSON.stringify(newBookmarks));
    } catch (error) {
      console.error('Error toggling bookmark:', error);
    }
  };

  const toggleDownload = async (paperId) => {
    try {
      let newDownloads;
      if (downloadedPapers.includes(paperId)) {
        newDownloads = downloadedPapers.filter(id => id !== paperId);
      } else {
        newDownloads = [...downloadedPapers, paperId];
      }
      
      setDownloadedPapers(newDownloads);
      await AsyncStorage.setItem('downloadedPapers', JSON.stringify(newDownloads));
      
      if (!downloadedPapers.includes(paperId)) {
        Alert.alert(
          'Paper Downloaded',
          'The research paper has been saved to your device.',
          [{ text: 'OK' }]
        );
      }
    } catch (error) {
      console.error('Error toggling download:', error);
    }
  };

  const openPDF = (paper) => {
    if (!isOnline && !downloadedPapers.includes(paper.id)) {
      Alert.alert(
        'Offline Mode',
        'You need to be online to view this paper, or download it for offline reading.',
        [
          { text: 'Cancel', style: 'cancel' },
          { text: 'Download', onPress: () => toggleDownload(paper.id) }
        ]
      );
      return;
    }
    
    setSelectedPaper(paper);
    setPdfModalVisible(true);
  };

  const sharePaper = async (paper) => {
    try {
      await Share.share({
        title: paper.title,
        message: `Check out this research paper: "${paper.title}"\n\nAuthors: ${paper.authors.join(', ')}\n\nDOI: ${paper.doi}`,
        url: paper.pdfUrl,
      });
    } catch (error) {
      console.error('Error sharing paper:', error);
    }
  };

  const copyCitation = async (paper) => {
    const citation = `${paper.authors.join(', ')} (${paper.year}). ${paper.title}. ${paper.journal}, ${paper.volume}(${paper.issue}), ${paper.pages}. DOI: ${paper.doi}`;
    
    await Clipboard.setString(citation);
    Alert.alert('Success', 'Citation copied to clipboard!');
  };

  const generateAISummary = async (paper) => {
    setGeneratingSummary(true);
    setSelectedPaper(paper);
    setSummaryModalVisible(true);
    setAiSummary('');

    // Local template used if the AI service is unavailable.
    const fallbackSummary = `🤖 AI Summary of "${paper.title}"\n\n📊 Key Findings:\n• ${String(paper.category).charAt(0).toUpperCase() + String(paper.category).slice(1)} research presenting novel approaches\n• Focus areas: ${(paper.keywords || []).slice(0, 2).join(' and ') || 'engineering'}\n• ${paper.citationCount}+ citations indicating impact\n\n🎯 Practical Applications:\n• ${paper.university} research with industry relevance\n• Open Access: ${paper.isOpenAccess ? '✅ Yes' : '❌ No'}\n\n📈 Impact Score: ${Math.round((paper.citationCount || 0) / 2 + (paper.downloadCount || 0) / 100)}/100\n\n💡 Recommendation: ${(paper.citationCount || 0) > 100 ? 'Highly influential paper worth studying in detail' : 'Emerging research with promising results'}`;

    try {
      const prompt = `Summarise this academic research paper for an engineering student. Use short sections with these headings: Key Findings, Methods, Practical Applications, and Recommendation. Keep it concise and readable.\n\nTitle: ${paper.title}\nAuthors: ${(paper.authors || []).join(', ')}\nUniversity: ${paper.university}\nJournal: ${paper.journal} (${paper.year})\nKeywords: ${(paper.keywords || []).join(', ')}\nCitations: ${paper.citationCount}\n\nAbstract:\n${paper.abstract || 'No abstract provided.'}`;

      const res = await fetch(`${RP_API_BASE}/api/ai`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-api-key': RP_API_KEY },
        body: JSON.stringify({ prompt }),
      }).then((r) => r.json()).catch(() => null);

      const text =
        res?.text ||
        res?.content ||
        res?.response ||
        res?.message ||
        res?.choices?.[0]?.message?.content ||
        '';

      setAiSummary(text && String(text).trim() ? String(text).trim() : fallbackSummary);
    } catch (e) {
      setAiSummary(fallbackSummary);
    } finally {
      setGeneratingSummary(false);
    }
  };

  const openDoiLink = (doi) => {
    const doiUrl = `https://doi.org/${doi}`;
    Linking.openURL(doiUrl).catch(err => {
      console.error('Failed to open DOI:', err);
      Alert.alert('Error', 'Could not open DOI link');
    });
  };

  const renderHeader = () => (
    <LinearGradient
      colors={darkMode ? ['#2d3748', '#4a5568'] : ['#6366f1', '#8b5cf6']}
      style={styles.header}
    >
      <TouchableOpacity 
        style={styles.backButton}
        onPress={() => navigation.goBack()}
      >
        <MaterialCommunityIcons name="arrow-left" size={22} color="#ffffff" />
      </TouchableOpacity>
      <View style={styles.headerCenter}>
        <Text style={styles.headerTitle}>📚 Research Papers</Text>
        <Text style={styles.headerSubtitle}>
          {filteredPapers.length} papers
        </Text>
      </View>
      <TouchableOpacity 
        style={styles.viewModeButton}
        onPress={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
      >
        <MaterialCommunityIcons 
          name={viewMode === 'grid' ? 'view-list' : 'view-grid'} 
          size={22} 
          color="#ffffff" 
        />
      </TouchableOpacity>
    </LinearGradient>
  );

  const renderSearchBar = () => (
    <View style={styles.searchContainer}>
      <MaterialCommunityIcons 
        name="magnify" 
        size={18} 
        color={darkMode ? "#a0aec0" : "#6b7280"} 
        style={styles.searchIcon}
      />
      <TextInput
        style={styles.searchInput}
        placeholder="Search papers, authors, keywords..."
        value={searchQuery}
        onChangeText={handleSearch}
        placeholderTextColor={darkMode ? "#a0aec0" : "#6b7280"}
      />
      {searchQuery.length > 0 && (
        <TouchableOpacity onPress={() => setSearchQuery('')}>
          <MaterialCommunityIcons 
            name="close-circle" 
            size={18} 
            color={darkMode ? "#a0aec0" : "#6b7280"} 
          />
        </TouchableOpacity>
      )}
    </View>
  );

  const renderFilterChips = () => (
    <View style={styles.filterContainer}>
      <ScrollView 
        horizontal 
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.filterScrollContent}
      >
        <TouchableOpacity
          style={styles.filterButton}
          onPress={() => setShowFilters(!showFilters)}
        >
          <MaterialCommunityIcons 
            name="filter-variant" 
            size={14} 
            color="#ffffff" 
          />
          <Text style={styles.filterButtonText}>Filters</Text>
        </TouchableOpacity>

        {selectedCategory !== 'all' && (
          <TouchableOpacity
            style={styles.activeFilterChip}
            onPress={() => setSelectedCategory('all')}
          >
            <Text style={styles.activeFilterText}>{selectedCategory}</Text>
            <MaterialCommunityIcons name="close" size={12} color="#ffffff" />
          </TouchableOpacity>
        )}

        {selectedYear !== 'all' && (
          <TouchableOpacity
            style={styles.activeFilterChip}
            onPress={() => setSelectedYear('all')}
          >
            <Text style={styles.activeFilterText}>{selectedYear}</Text>
            <MaterialCommunityIcons name="close" size={12} color="#ffffff" />
          </TouchableOpacity>
        )}

        {selectedUniversity !== 'all' && (
          <TouchableOpacity
            style={styles.activeFilterChip}
            onPress={() => setSelectedUniversity('all')}
          >
            <Text style={styles.activeFilterText}>{selectedUniversity}</Text>
            <MaterialCommunityIcons name="close" size={12} color="#ffffff" />
          </TouchableOpacity>
        )}
      </ScrollView>
    </View>
  );

  const renderFiltersModal = () => (
    <Modal
      visible={showFilters}
      animationType="slide"
      transparent={true}
      onRequestClose={() => setShowFilters(false)}
    >
      <View style={styles.modalOverlay}>
        <View style={[styles.modalContent, { backgroundColor: darkMode ? '#2d3748' : '#ffffff' }]}>
          <View style={styles.modalHeader}>
            <View style={styles.modalTitleContainer}>
              <MaterialCommunityIcons name="filter-variant" size={18} color={darkMode ? '#ffffff' : '#1f2937'} />
              <Text style={[styles.modalTitle, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
                Filters
              </Text>
            </View>
            <TouchableOpacity onPress={() => setShowFilters(false)}>
              <MaterialCommunityIcons name="close" size={20} color={darkMode ? '#a0aec0' : '#6b7280'} />
            </TouchableOpacity>
          </View>

          <ScrollView style={styles.filterOptions}>
            <View style={styles.filterSection}>
              <Text style={[styles.filterSectionTitle, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                Sort By
              </Text>
              <View style={styles.sortOptions}>
                {[
                  { id: 'date', label: 'Newest', icon: 'calendar' },
                  { id: 'citations', label: 'Citations', icon: 'chart-bar' },
                  { id: 'downloads', label: 'Downloads', icon: 'download' },
                  { id: 'title', label: 'A-Z', icon: 'sort-alphabetical' },
                ].map((option) => (
                  <TouchableOpacity
                    key={option.id}
                    style={[
                      styles.sortOption,
                      sortBy === option.id && styles.sortOptionActive
                    ]}
                    onPress={() => setSortBy(option.id)}
                  >
                    <MaterialCommunityIcons 
                      name={option.icon} 
                      size={14} 
                      color={sortBy === option.id ? '#ffffff' : (darkMode ? '#a0aec0' : '#6b7280')} 
                    />
                    <Text style={[
                      styles.sortOptionText,
                      sortBy === option.id && styles.sortOptionTextActive
                    ]}>
                      {option.label}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>

            <View style={styles.filterSection}>
              <Text style={[styles.filterSectionTitle, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                Category
              </Text>
              <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                <View style={styles.categoryOptions}>
                  {categories.map((category) => (
                    <TouchableOpacity
                      key={category.id}
                      style={[
                        styles.categoryChip,
                        selectedCategory === category.id && styles.categoryChipActive
                      ]}
                      onPress={() => setSelectedCategory(category.id)}
                    >
                      <MaterialCommunityIcons 
                        name={category.icon} 
                        size={14} 
                        color={selectedCategory === category.id ? '#ffffff' : (darkMode ? '#a0aec0' : '#6b7280')} 
                      />
                      <Text style={[
                        styles.categoryChipText,
                        selectedCategory === category.id && styles.categoryChipTextActive
                      ]}>
                        {category.label}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </ScrollView>
            </View>

            <View style={styles.filterSection}>
              <Text style={[styles.filterSectionTitle, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                Year
              </Text>
              <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                <View style={styles.yearOptions}>
                  {years.map((year) => (
                    <TouchableOpacity
                      key={year.id}
                      style={[
                        styles.yearChip,
                        selectedYear === year.id && styles.yearChipActive
                      ]}
                      onPress={() => setSelectedYear(year.id)}
                    >
                      <Text style={[
                        styles.yearChipText,
                        selectedYear === year.id && styles.yearChipTextActive
                      ]}>
                        {year.label}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </ScrollView>
            </View>

            <View style={styles.filterSection}>
              <Text style={[styles.filterSectionTitle, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                University
              </Text>
              <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                <View style={styles.universityOptions}>
                  {universities.map((uni) => (
                    <TouchableOpacity
                      key={uni.id}
                      style={[
                        styles.universityChip,
                        selectedUniversity === uni.id && styles.universityChipActive
                      ]}
                      onPress={() => setSelectedUniversity(uni.id)}
                    >
                      <Text style={[
                        styles.universityChipText,
                        selectedUniversity === uni.id && styles.universityChipTextActive
                      ]}>
                        {uni.label}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </ScrollView>
            </View>
          </ScrollView>

          <View style={styles.filterActions}>
            <TouchableOpacity 
              style={styles.resetButton}
              onPress={() => {
                setSelectedCategory('all');
                setSelectedYear('all');
                setSelectedUniversity('all');
                setSortBy('date');
              }}
            >
              <Text style={styles.resetButtonText}>Reset All</Text>
            </TouchableOpacity>
            <TouchableOpacity 
              style={styles.applyButton}
              onPress={() => setShowFilters(false)}
            >
              <Text style={styles.applyButtonText}>Apply</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );

  const getCategoryColor = (category) => {
    const colors = darkMode ? {
      mechanical: ['#dd6b20', '#ed8936'],
      civil: ['#2b6cb0', '#4299e1'],
      electrical: ['#d69e2e', '#ecc94b'],
      computer: ['#4c51bf', '#6366f1'],
      chemical: ['#38a169', '#68d391'],
      aerospace: ['#9f7aea', '#b794f4'],
      biomedical: ['#ed64a6', '#f687b3'],
      default: ['#718096', '#a0aec0'],
    } : {
      mechanical: ['#f97316', '#fb923c'],
      civil: ['#3b82f6', '#60a5fa'],
      electrical: ['#eab308', '#facc15'],
      computer: ['#6366f1', '#818cf8'],
      chemical: ['#10b981', '#34d399'],
      aerospace: ['#8b5cf6', '#a78bfa'],
      biomedical: ['#ec4899', '#f472b6'],
      default: ['#6b7280', '#9ca3af'],
    };
    
    return colors[category] || colors.default;
  };

  const getCategoryIcon = (category) => {
    const icons = {
      mechanical: 'cog',
      civil: 'bridge',
      electrical: 'flash',
      computer: 'laptop',
      chemical: 'flask',
      aerospace: 'airplane',
      biomedical: 'heart-pulse',
      default: 'file-document',
    };
    
    return icons[category] || icons.default;
  };

  const renderEmptyState = () => (
    <View style={styles.emptyState}>
      <MaterialCommunityIcons 
        name="file-document-search" 
        size={48} 
        color={darkMode ? "#4a5568" : "#d1d5db"} 
      />
      <Text style={[styles.emptyStateTitle, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
        No Papers Found
      </Text>
      <Text style={[styles.emptyStateText, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
        Try adjusting your filters or search terms
      </Text>
      <TouchableOpacity 
        style={styles.resetFiltersButton}
        onPress={() => {
          setSearchQuery('');
          setSelectedCategory('all');
          setSelectedYear('all');
          setSelectedUniversity('all');
        }}
      >
        <Text style={styles.resetFiltersText}>Reset All Filters</Text>
      </TouchableOpacity>
    </View>
  );

  const renderPDFModal = () => (
    <Modal
      visible={pdfModalVisible}
      animationType="slide"
      onRequestClose={() => setPdfModalVisible(false)}
    >
      <SafeAreaView style={[styles.pdfModalContainer, { backgroundColor: darkMode ? '#1a202c' : '#ffffff' }]}>
        <LinearGradient
          colors={darkMode ? ['#2d3748', '#4a5568'] : ['#6366f1', '#8b5cf6']}
          style={styles.pdfHeader}
        >
          <TouchableOpacity 
            style={styles.pdfBackButton}
            onPress={() => setPdfModalVisible(false)}
          >
            <MaterialCommunityIcons name="close" size={22} color="#ffffff" />
          </TouchableOpacity>
          <View style={styles.pdfHeaderCenter}>
            <Text style={styles.pdfTitle} numberOfLines={1}>
              {selectedPaper?.title}
            </Text>
            <Text style={styles.pdfSubtitle}>
              {selectedPaper?.authors[0]} • {selectedPaper?.year}
            </Text>
          </View>
          <TouchableOpacity 
            style={styles.pdfDownloadButton}
            onPress={() => selectedPaper && toggleDownload(selectedPaper.id)}
          >
            <MaterialCommunityIcons 
              name={downloadedPapers.includes(selectedPaper?.id) ? "check" : "download"} 
              size={22} 
              color="#ffffff" 
            />
          </TouchableOpacity>
        </LinearGradient>

        {selectedPaper && (
          <WebView
            source={{ uri: selectedPaper.pdfUrl || undefined }}
            style={styles.pdfViewer}
            startInLoadingState={true}
            renderLoading={() => (
              <View style={styles.pdfLoading}>
                <ActivityIndicator size="large" color="#6366f1" />
                <Text style={styles.pdfLoadingText}>Loading PDF...</Text>
              </View>
            )}
          />
        )}

        <View style={[styles.pdfFooter, { backgroundColor: darkMode ? '#2d3748' : '#ffffff' }]}>
          <TouchableOpacity 
            style={styles.pdfActionButton}
            onPress={() => selectedPaper && copyCitation(selectedPaper)}
          >
            <MaterialCommunityIcons name="content-copy" size={18} color={darkMode ? "#a0aec0" : "#6b7280"} />
            <Text style={[styles.pdfActionText, { color: darkMode ? "#a0aec0" : "#6b7280" }]}>
              Citation
            </Text>
          </TouchableOpacity>
          
          <TouchableOpacity 
            style={styles.pdfActionButton}
            onPress={() => selectedPaper && sharePaper(selectedPaper)}
          >
            <MaterialCommunityIcons name="share-variant" size={18} color={darkMode ? "#a0aec0" : "#6b7280"} />
            <Text style={[styles.pdfActionText, { color: darkMode ? "#a0aec0" : "#6b7280" }]}>
              Share
            </Text>
          </TouchableOpacity>
          
          <TouchableOpacity 
            style={styles.pdfActionButton}
            onPress={() => selectedPaper && openDoiLink(selectedPaper.doi)}
          >
            <MaterialCommunityIcons name="link" size={18} color={darkMode ? "#a0aec0" : "#6b7280"} />
            <Text style={[styles.pdfActionText, { color: darkMode ? "#a0aec0" : "#6b7280" }]}>
              DOI
            </Text>
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    </Modal>
  );

  const renderSummaryModal = () => (
    <Modal
      visible={summaryModalVisible}
      animationType="slide"
      transparent={true}
      onRequestClose={() => setSummaryModalVisible(false)}
    >
      <View style={styles.modalOverlay}>
        <View style={[styles.summaryModalContent, { backgroundColor: darkMode ? '#2d3748' : '#ffffff' }]}>
          <View style={styles.summaryHeader}>
            <LinearGradient
              colors={darkMode ? ['#4c51bf', '#6366f1'] : ['#6366f1', '#8b5cf6']}
              style={styles.summaryHeaderGradient}
            >
              <MaterialCommunityIcons name="robot" size={28} color="#ffffff" />
              <Text style={styles.summaryTitle}>🤖 AI Summary</Text>
            </LinearGradient>
            <TouchableOpacity 
              style={styles.summaryCloseButton}
              onPress={() => setSummaryModalVisible(false)}
            >
              <MaterialCommunityIcons name="close" size={20} color={darkMode ? '#a0aec0' : '#6b7280'} />
            </TouchableOpacity>
          </View>

          {generatingSummary ? (
            <View style={styles.summaryLoading}>
              <ActivityIndicator size="large" color="#6366f1" />
              <Text style={[styles.summaryLoadingText, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                Generating summary...
              </Text>
            </View>
          ) : (
            <ScrollView style={styles.summaryContent}>
              <Text style={[styles.paperTitleSummary, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
                {selectedPaper?.title}
              </Text>
              
              <View style={styles.paperMetaSummary}>
                <View style={styles.metaItemSummary}>
                  <MaterialCommunityIcons name="account" size={12} color={darkMode ? "#a0aec0" : "#6b7280"} />
                  <Text style={[styles.metaTextSummary, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                    {selectedPaper?.authors.join(', ')}
                  </Text>
                </View>
                <View style={styles.metaItemSummary}>
                  <MaterialCommunityIcons name="school" size={12} color={darkMode ? "#a0aec0" : "#6b7280"} />
                  <Text style={[styles.metaTextSummary, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                    {selectedPaper?.university}
                  </Text>
                </View>
              </View>

              <Text style={[styles.summaryText, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}>
                {aiSummary}
              </Text>

              <View style={styles.summaryStats}>
                <View style={styles.summaryStatItem}>
                  <MaterialCommunityIcons name="chart-bar" size={18} color="#f59e0b" />
                  <Text style={styles.summaryStatNumber}>{selectedPaper?.citationCount}</Text>
                  <Text style={[styles.summaryStatLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                    Citations
                  </Text>
                </View>
                <View style={styles.summaryStatItem}>
                  <MaterialCommunityIcons name="download" size={18} color="#10b981" />
                  <Text style={styles.summaryStatNumber}>{selectedPaper?.downloadCount}</Text>
                  <Text style={[styles.summaryStatLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                    Downloads
                  </Text>
                </View>
                <View style={styles.summaryStatItem}>
                  <MaterialCommunityIcons name="star" size={18} color="#8b5cf6" />
                  <Text style={styles.summaryStatNumber}>{selectedPaper?.impactFactor}</Text>
                  <Text style={[styles.summaryStatLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                    Impact
                  </Text>
                </View>
              </View>

              <View style={styles.summaryActions}>
                <TouchableOpacity 
                  style={styles.readFullButton}
                  onPress={() => {
                    setSummaryModalVisible(false);
                    openPDF(selectedPaper);
                  }}
                >
                  <Text style={styles.readFullText}>Read Full Paper</Text>
                  <MaterialCommunityIcons name="arrow-right" size={14} color="#6366f1" />
                </TouchableOpacity>
              </View>
            </ScrollView>
          )}
        </View>
      </View>
    </Modal>
  );

  const renderPaperGrid = ({ item, index }) => {
    const isBookmarked = bookmarkedPapers.includes(item.id);
    
    return (
      <TouchableOpacity
        style={styles.paperCardGrid}
        onPress={() => openPDF(item)}
        activeOpacity={0.8}
      >
        <View style={styles.paperHeaderGrid}>
          <LinearGradient
            colors={getCategoryColor(item.category)}
            style={styles.paperCategoryGrid}
          >
            <Text style={styles.paperCategoryText}>{item.category.charAt(0).toUpperCase()}</Text>
          </LinearGradient>
          
          <TouchableOpacity 
            style={styles.bookmarkButtonGrid}
            onPress={() => toggleBookmark(item.id)}
          >
            <MaterialCommunityIcons 
              name={isBookmarked ? "bookmark" : "bookmark-outline"} 
              size={18} 
              color={isBookmarked ? "#6366f1" : (darkMode ? "#a0aec0" : "#6b7280")} 
            />
          </TouchableOpacity>
        </View>

        <View style={styles.paperContentGrid}>
          <Text style={styles.paperTitleGrid} numberOfLines={3}>
            {item.title}
          </Text>
          
          <View style={styles.paperAuthorsGrid}>
            <Text style={styles.paperAuthorsText} numberOfLines={1}>
              {item.authors[0]} {item.authors.length > 1 ? `+${item.authors.length - 1}` : ''}
            </Text>
          </View>

          <View style={styles.paperMetaGrid}>
            <View style={styles.metaItem}>
              <MaterialCommunityIcons name="school" size={10} color={darkMode ? "#a0aec0" : "#6b7280"} />
              <Text style={styles.metaText}>{item.university}</Text>
            </View>
            <View style={styles.metaItem}>
              <MaterialCommunityIcons name="calendar" size={10} color={darkMode ? "#a0aec0" : "#6b7280"} />
              <Text style={styles.metaText}>{item.year}</Text>
            </View>
          </View>

          <View style={styles.paperStatsGrid}>
            <View style={styles.statItem}>
              <MaterialCommunityIcons name="chart-bar" size={10} color="#f59e0b" />
              <Text style={styles.statText}>{item.citationCount}</Text>
            </View>
            <View style={styles.statItem}>
              <MaterialCommunityIcons name="download" size={10} color="#10b981" />
              <Text style={styles.statText}>{item.downloadCount}</Text>
            </View>
          </View>
        </View>

        <View style={styles.paperActionsGrid}>
          <TouchableOpacity 
            style={styles.actionButton}
            onPress={() => openPDF(item)}
          >
            <MaterialCommunityIcons name="file-pdf-box" size={14} color="#6366f1" />
            <Text style={styles.actionText}>Read</Text>
          </TouchableOpacity>
          
          <TouchableOpacity 
            style={styles.actionButton}
            onPress={() => generateAISummary(item)}
          >
            <MaterialCommunityIcons name="robot" size={14} color="#8b5cf6" />
            <Text style={styles.actionText}>AI</Text>
          </TouchableOpacity>
        </View>
      </TouchableOpacity>
    );
  };

  const renderPaperList = ({ item, index }) => {
    const isBookmarked = bookmarkedPapers.includes(item.id);
    
    return (
      <TouchableOpacity
        style={styles.paperCardList}
        onPress={() => openPDF(item)}
        activeOpacity={0.8}
      >
        <LinearGradient
          colors={getCategoryColor(item.category)}
          style={styles.paperCategoryList}
        >
          <MaterialCommunityIcons name={getCategoryIcon(item.category)} size={18} color="#ffffff" />
        </LinearGradient>

        <View style={styles.paperContentList}>
          <View style={styles.paperHeaderList}>
            <Text style={styles.paperTitleList} numberOfLines={2}>
              {item.title}
            </Text>
            <TouchableOpacity 
              style={styles.bookmarkButtonList}
              onPress={() => toggleBookmark(item.id)}
            >
              <MaterialCommunityIcons 
                name={isBookmarked ? "bookmark" : "bookmark-outline"} 
                size={18} 
                color={isBookmarked ? "#6366f1" : (darkMode ? "#a0aec0" : "#6b7280")} 
              />
            </TouchableOpacity>
          </View>

          <Text style={styles.paperAuthorsList} numberOfLines={1}>
            {item.authors.join(', ')}
          </Text>

          <View style={styles.paperDetailsList}>
            <View style={styles.detailItem}>
              <MaterialCommunityIcons name="school" size={12} color={darkMode ? "#a0aec0" : "#6b7280"} />
              <Text style={styles.detailText}>{item.university}</Text>
            </View>
            <View style={styles.detailItem}>
              <MaterialCommunityIcons name="calendar" size={12} color={darkMode ? "#a0aec0" : "#6b7280"} />
              <Text style={styles.detailText}>{item.year}</Text>
            </View>
          </View>

          <View style={styles.paperStatsList}>
            <View style={styles.statItemList}>
              <MaterialCommunityIcons name="chart-bar" size={12} color="#f59e0b" />
              <Text style={styles.statTextList}>{item.citationCount} citations</Text>
            </View>
            <View style={styles.statItemList}>
              <MaterialCommunityIcons name="download" size={12} color="#10b981" />
              <Text style={styles.statTextList}>{item.downloadCount} downloads</Text>
            </View>
          </View>

          <View style={styles.paperActionsList}>
            <TouchableOpacity 
              style={[styles.actionButtonList, styles.readButton]}
              onPress={() => openPDF(item)}
            >
              <MaterialCommunityIcons name="file-pdf-box" size={14} color="#ffffff" />
              <Text style={styles.actionTextList}>Read</Text>
            </TouchableOpacity>
            
            <TouchableOpacity 
              style={[styles.actionButtonList, styles.summaryButton]}
              onPress={() => generateAISummary(item)}
            >
              <MaterialCommunityIcons name="robot" size={14} color="#ffffff" />
              <Text style={styles.actionTextList}>AI Summary</Text>
            </TouchableOpacity>
          </View>
        </View>
      </TouchableOpacity>
    );
  };

  const renderHeaderComponent = () => (
    <>
      {!isOnline && (
        <View style={styles.offlineBanner}>
          <MaterialCommunityIcons name="wifi-off" size={14} color="#ffffff" />
          <Text style={styles.offlineText}>Offline Mode - Downloaded papers only</Text>
        </View>
      )}

      <View style={styles.statsOverview}>
        <View style={[styles.statCard, { backgroundColor: darkMode ? '#2d3748' : '#ffffff' }]}>
          <MaterialCommunityIcons name="file-document" size={20} color="#6366f1" />
          <Text style={[styles.statNumber, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
            {researchPapers.length}
          </Text>
          <Text style={[styles.statLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
            Papers
          </Text>
        </View>
        
        <View style={[styles.statCard, { backgroundColor: darkMode ? '#2d3748' : '#ffffff' }]}>
          <MaterialCommunityIcons name="chart-bar" size={20} color="#f59e0b" />
          <Text style={[styles.statNumber, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
            {researchPapers.reduce((acc, paper) => acc + paper.citationCount, 0).toLocaleString()}
          </Text>
          <Text style={[styles.statLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
            Citations
          </Text>
        </View>
        
        <View style={[styles.statCard, { backgroundColor: darkMode ? '#2d3748' : '#ffffff' }]}>
          <MaterialCommunityIcons name="school" size={20} color="#10b981" />
          <Text style={[styles.statNumber, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
            {new Set(researchPapers.map(p => p.university)).size}
          </Text>
          <Text style={[styles.statLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
            Unis
          </Text>
        </View>
        
        <View style={[styles.statCard, { backgroundColor: darkMode ? '#2d3748' : '#ffffff' }]}>
          <MaterialCommunityIcons name="download" size={20} color="#8b5cf6" />
          <Text style={[styles.statNumber, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
            {researchPapers.reduce((acc, paper) => acc + paper.downloadCount, 0).toLocaleString().slice(0, 3)}k
          </Text>
          <Text style={[styles.statLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
            Downloads
          </Text>
        </View>
      </View>

      <View style={styles.contentContainer}>
        <View style={styles.sectionHeader}>
          <Text style={[styles.sectionTitle, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
            Research Papers ({filteredPapers.length})
          </Text>
          <TouchableOpacity onPress={() => setShowFilters(true)}>
            <Text style={styles.filterLink}>Filter & Sort</Text>
          </TouchableOpacity>
        </View>
      </View>
    </>
  );

  if (loading) {
    return (
      <SafeAreaView style={[styles.loadingContainer, { backgroundColor: darkMode ? '#1a202c' : '#f0f4f8' }]}>
        <ActivityIndicator size="large" color="#6366f1" />
        <Text style={[styles.loadingText, { color: darkMode ? '#cbd5e0' : '#6b7280' }]}>
          Loading research papers...
        </Text>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={[styles.safeArea, { backgroundColor: darkMode ? '#1a202c' : '#f0f4f8' }]}>
      <StatusBar barStyle={darkMode ? "light-content" : "light-content"} backgroundColor="#6366f1" />
      
      {renderHeader()}
      {renderSearchBar()}
      {renderFilterChips()}
      
      <FlatList
        ref={flatListRef}
        data={filteredPapers}
        key={viewMode === 'grid' ? 'grid' : 'list'}
        keyExtractor={(item) => item.id}
        numColumns={viewMode === 'grid' ? 2 : 1}
        contentContainerStyle={styles.scrollContent}
        ListHeaderComponent={renderHeaderComponent}
        ListEmptyComponent={renderEmptyState}
        renderItem={viewMode === 'grid' ? renderPaperGrid : renderPaperList}
        refreshControl={
          <RefreshControl
            refreshing={isRefreshing}
            onRefresh={refreshData}
            colors={['#6366f1']}
            tintColor="#6366f1"
          />
        }
        showsVerticalScrollIndicator={false}
        style={styles.scrollView}
      />

      {renderFiltersModal()}
      {renderPDFModal()}
      {renderSummaryModal()}

      <TouchableOpacity 
        style={styles.fab}
        onPress={() => setShowFilters(true)}
      >
        <LinearGradient
          colors={['#6366f1', '#8b5cf6']}
          style={styles.fabGradient}
        >
          <MaterialCommunityIcons name="filter-variant" size={20} color="#ffffff" />
        </LinearGradient>
      </TouchableOpacity>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 12,
    fontSize: 14,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 15,
  },
  backButton: {
    padding: 6,
  },
  headerCenter: {
    flex: 1,
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#ffffff',
  },
  headerSubtitle: {
    fontSize: 11,
    color: 'rgba(255, 255, 255, 0.8)',
    marginTop: 2,
  },
  viewModeButton: {
    padding: 6,
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    marginHorizontal: 16,
    marginVertical: 8,
    borderRadius: 10,
    paddingHorizontal: 12,
    height: 40,
  },
  searchIcon: {
    marginRight: 8,
  },
  searchInput: {
    flex: 1,
    height: 40,
    fontSize: 14,
    color: '#ffffff',
  },
  filterContainer: {
    height: 32,
    marginBottom: 4,
  },
  filterScrollContent: {
    paddingHorizontal: 16,
    alignItems: 'center',
  },
  filterButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#6366f1',
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 16,
    marginRight: 6,
    height: 24,
  },
  filterButtonText: {
    color: '#ffffff',
    fontSize: 11,
    fontWeight: '500',
    marginLeft: 4,
  },
  activeFilterChip: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#8b5cf6',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 16,
    marginRight: 6,
    height: 24,
  },
  activeFilterText: {
    color: '#ffffff',
    fontSize: 10,
    fontWeight: '500',
    marginRight: 3,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingBottom: 16,
  },
  offlineBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#6b7280',
    paddingVertical: 6,
    marginHorizontal: 16,
    borderRadius: 6,
    marginTop: 4,
    marginBottom: 8,
  },
  offlineText: {
    color: '#ffffff',
    fontSize: 11,
    marginLeft: 6,
  },
  statsOverview: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    paddingHorizontal: 16,
    marginBottom: 12,
    gap: 8,
  },
  statCard: {
    flex: 1,
    minWidth: '45%',
    alignItems: 'center',
    padding: 10,
    borderRadius: 10,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.08,
    shadowRadius: 3,
    elevation: 2,
  },
  statNumber: {
    fontSize: 16,
    fontWeight: 'bold',
    marginVertical: 4,
  },
  statLabel: {
    fontSize: 10,
  },
  contentContainer: {
    paddingHorizontal: 16,
    marginBottom: 8,
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 'bold',
  },
  filterLink: {
    fontSize: 12,
    color: '#6366f1',
    fontWeight: '600',
  },
  paperCardGrid: {
    flex: 1,
    margin: 6,
    backgroundColor: '#ffffff',
    borderRadius: 16,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.08,
    shadowRadius: 4,
    elevation: 2,
    minHeight: 160,
  },
  paperHeaderGrid: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 10,
    paddingTop: 10,
  },
  paperCategoryGrid: {
    width: 24,
    height: 24,
    borderRadius: 6,
    justifyContent: 'center',
    alignItems: 'center',
  },
  paperCategoryText: {
    fontSize: 10,
    fontWeight: 'bold',
    color: '#ffffff',
  },
  bookmarkButtonGrid: {
    padding: 2,
  },
  paperContentGrid: {
    padding: 10,
    flex: 1,
  },
  paperTitleGrid: {
    fontSize: 12,
    fontWeight: '600',
    color: '#1f2937',
    marginBottom: 6,
    lineHeight: 16,
  },
  paperAuthorsGrid: {
    marginBottom: 6,
  },
  paperAuthorsText: {
    fontSize: 10,
    color: '#6b7280',
  },
  paperMetaGrid: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 6,
  },
  metaItem: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  metaText: {
    fontSize: 9,
    color: '#6b7280',
    marginLeft: 3,
  },
  paperStatsGrid: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    marginTop: 4,
  },
  statItem: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  statText: {
    fontSize: 10,
    fontWeight: '600',
    color: '#374151',
    marginLeft: 3,
  },
  paperActionsGrid: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    paddingVertical: 8,
    paddingHorizontal: 6,
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
  },
  actionButton: {
    alignItems: 'center',
  },
  actionText: {
    fontSize: 9,
    color: '#6b7280',
    marginTop: 2,
  },
  paperCardList: {
    flexDirection: 'row',
    backgroundColor: '#ffffff',
    borderRadius: 16,
    marginBottom: 8,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.08,
    shadowRadius: 3,
    elevation: 2,
    marginHorizontal: 16,
    minHeight: 100,
  },
  paperCategoryList: {
    width: 48,
    justifyContent: 'center',
    alignItems: 'center',
  },
  paperContentList: {
    flex: 1,
    padding: 12,
  },
  paperHeaderList: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 6,
  },
  paperTitleList: {
    flex: 1,
    fontSize: 14,
    fontWeight: 'bold',
    color: '#1f2937',
    lineHeight: 18,
    marginRight: 6,
  },
  bookmarkButtonList: {
    padding: 2,
  },
  paperAuthorsList: {
    fontSize: 11,
    color: '#6b7280',
    marginBottom: 6,
  },
  paperDetailsList: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
    marginBottom: 8,
  },
  detailItem: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  detailText: {
    fontSize: 11,
    color: '#6b7280',
    marginLeft: 4,
  },
  paperStatsList: {
    flexDirection: 'row',
    gap: 12,
    marginBottom: 8,
  },
  statItemList: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  statTextList: {
    fontSize: 11,
    fontWeight: '600',
    color: '#374151',
    marginLeft: 4,
  },
  paperActionsList: {
    flexDirection: 'row',
    gap: 6,
  },
  actionButtonList: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 26,
    borderRadius: 6,
    flex: 1,
    justifyContent: 'center',
  },
  readButton: {
    backgroundColor: '#6366f1',
  },
  summaryButton: {
    backgroundColor: '#8b5cf6',
  },
  actionTextList: {
    color: '#ffffff',
    fontSize: 11,
    fontWeight: '600',
    marginLeft: 4,
  },
  emptyState: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 40,
    paddingHorizontal: 16,
  },
  emptyStateTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    marginTop: 12,
  },
  emptyStateText: {
    fontSize: 13,
    marginTop: 6,
    textAlign: 'center',
  },
  resetFiltersButton: {
    backgroundColor: '#6366f1',
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 6,
    marginTop: 12,
  },
  resetFiltersText: {
    color: '#ffffff',
    fontSize: 13,
    fontWeight: '600',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  modalContent: {
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    maxHeight: '70%',
    paddingBottom: Platform.OS === 'ios' ? 34 : 16,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 14,
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
  },
  modalTitleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  modalTitle: {
    fontSize: 16,
    fontWeight: 'bold',
  },
  filterOptions: {
    paddingHorizontal: 14,
    paddingTop: 10,
  },
  filterSection: {
    marginBottom: 16,
  },
  filterSectionTitle: {
    fontSize: 12,
    fontWeight: '600',
    marginBottom: 8,
  },
  sortOptions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
  },
  sortOption: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f3f4f6',
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 6,
  },
  sortOptionActive: {
    backgroundColor: '#6366f1',
  },
  sortOptionText: {
    fontSize: 11,
    color: '#6b7280',
    marginLeft: 4,
  },
  sortOptionTextActive: {
    color: '#ffffff',
  },
  categoryOptions: {
    flexDirection: 'row',
    gap: 6,
    paddingVertical: 2,
  },
  categoryChip: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f3f4f6',
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 14,
  },
  categoryChipActive: {
    backgroundColor: '#6366f1',
  },
  categoryChipText: {
    fontSize: 11,
    color: '#6b7280',
    marginLeft: 4,
  },
  categoryChipTextActive: {
    color: '#ffffff',
  },
  yearOptions: {
    flexDirection: 'row',
    gap: 6,
    paddingVertical: 2,
  },
  yearChip: {
    backgroundColor: '#f3f4f6',
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 14,
  },
  yearChipActive: {
    backgroundColor: '#6366f1',
  },
  yearChipText: {
    fontSize: 11,
    color: '#6b7280',
  },
  yearChipTextActive: {
    color: '#ffffff',
  },
  universityOptions: {
    flexDirection: 'row',
    gap: 6,
    paddingVertical: 2,
  },
  universityChip: {
    backgroundColor: '#f3f4f6',
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 14,
  },
  universityChipActive: {
    backgroundColor: '#6366f1',
  },
  universityChipText: {
    fontSize: 11,
    color: '#6b7280',
  },
  universityChipTextActive: {
    color: '#ffffff',
  },
  filterActions: {
    flexDirection: 'row',
    paddingHorizontal: 14,
    paddingTop: 12,
    gap: 8,
  },
  resetButton: {
    flex: 1,
    backgroundColor: '#e5e7eb',
    paddingVertical: 40,
    borderRadius: 8,
    alignItems: 'center',
  },
  applyButton: {
    flex: 1,
    backgroundColor: '#6366f1',
    paddingVertical: 10,
    borderRadius: 8,
    alignItems: 'center',
  },
  resetButtonText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#6b7280',
  },
  applyButtonText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#ffffff',
  },
  fab: {
    position: 'absolute',
    bottom: 20,
    right: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 3 },
    shadowOpacity: 0.2,
    shadowRadius: 6,
    elevation: 6,
  },
  fabGradient: {
    width: 48,
    height: 48,
    borderRadius: 24,
    justifyContent: 'center',
    alignItems: 'center',
  },
  pdfModalContainer: {
    flex: 1,
  },
  pdfHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  pdfBackButton: {
    padding: 6,
  },
  pdfHeaderCenter: {
    flex: 1,
    marginHorizontal: 10,
  },
  pdfTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#ffffff',
  },
  pdfSubtitle: {
    fontSize: 11,
    color: 'rgba(255, 255, 255, 0.8)',
    marginTop: 2,
  },
  pdfDownloadButton: {
    padding: 6,
  },
  pdfViewer: {
    flex: 1,
  },
  pdfLoading: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#ffffff',
  },
  pdfLoadingText: {
    marginTop: 10,
    fontSize: 14,
    color: '#6b7280',
  },
  pdfFooter: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    paddingVertical: 10,
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
  },
  pdfActionButton: {
    alignItems: 'center',
    flex: 1,
  },
  pdfActionText: {
    fontSize: 11,
    marginTop: 3,
  },
  summaryModalContent: {
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    maxHeight: '85%',
  },
  summaryHeader: {
    position: 'relative',
  },
  summaryHeaderGradient: {
    padding: 18,
    alignItems: 'center',
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
  },
  summaryTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#ffffff',
    marginTop: 8,
  },
  summaryCloseButton: {
    position: 'absolute',
    top: 14,
    right: 14,
    padding: 6,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    borderRadius: 16,
  },
  summaryLoading: {
    padding: 40,
    alignItems: 'center',
  },
  summaryLoadingText: {
    marginTop: 12,
    fontSize: 14,
  },
  summaryContent: {
    padding: 16,
  },
  paperTitleSummary: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 10,
  },
  paperMetaSummary: {
    marginBottom: 16,
  },
  metaItemSummary: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 6,
  },
  metaTextSummary: {
    fontSize: 12,
    marginLeft: 6,
  },
  summaryText: {
    fontSize: 13,
    lineHeight: 20,
    marginBottom: 20,
  },
  summaryStats: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    marginBottom: 20,
    backgroundColor: '#f3f4f6',
    padding: 12,
    borderRadius: 10,
  },
  summaryStatItem: {
    alignItems: 'center',
    flex: 1,
  },
  summaryStatNumber: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#1f2937',
    marginVertical: 6,
  },
  summaryStatLabel: {
    fontSize: 10,
  },
  summaryActions: {
    marginTop: 16,
  },
  readFullButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f3f4f6',
    paddingVertical: 12,
    borderRadius: 10,
  },
  readFullText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#6366f1',
    marginRight: 6,
  },
});

export default ResearchPapers;